import React, { useState, useEffect } from 'react'

const MainContent: React.FC = () => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0)
  const images = [
    '/board.jpg',
    '/connected.jpg',
    '/back.jpg'
  ]

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImageIndex((prevIndex) => (prevIndex + 1) % images.length)
    }, 6000) // Change image every few seconds

    return () => clearInterval(interval)
  }, [images.length])

  return (
    <section className="section-padding">
      <div className="max-w-4xl mx-auto text-lg leading-relaxed">
        <p className="text-2xl font-bold mb-2">
          <a href="https://www.youtube.com/embed/n2qBymMFUXo" className="whitespace-nowrap">Welcome</a> 
        </p>
        <p className="mb-4">We have released an open-source project</p>

        <div className="bg-black py-4 rounded-lg">
          <p className="text-2xl font-bold mb-4">
            The Smart Servo Add-On Board
          </p>
          {/* Mobile: Carousel between heading and paragraph */}
          <div className="flex justify-center mb-6 md:hidden">
            <div className="w-80">
              <div className="relative overflow-hidden rounded-lg" style={{ aspectRatio: '1/1' }}>
                <img
                  src={images[currentImageIndex]}
                  alt={`Smart Servo Add-On Board ${currentImageIndex + 1}`}
                  className="w-full h-full object-cover object-center transition-opacity duration-500"
                  style={{
                    objectPosition: currentImageIndex === 1 ? '35% center' : 'center center'
                  }}
                />
                <button
                  onClick={() => setCurrentImageIndex((prevIndex) => (prevIndex - 1 + images.length) % images.length)}
                  className="absolute left-2 top-1/2 transform -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white p-2 rounded-full transition-colors"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                </button>
                <button
                  onClick={() => setCurrentImageIndex((prevIndex) => (prevIndex + 1) % images.length)}
                  className="absolute right-2 top-1/2 transform -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white p-2 rounded-full transition-colors"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
          <div className="flex flex-col md:flex-row gap-12">
            <div className="flex-1">
              <p>
                This compact add-on board provides a seamless way to control TTL servos, such as Dynamixel or Feetech, using a Xiao ESP32. It offers three versatile modes for controlling your robot:
                <ul className="list-disc list-outside space-y-4 ml-10 mt-6">
                  <li><strong>Wireless Bridge:</strong> Use it as a wireless link between your robot and your computer, enabling real-time command and feedback.</li>
                  <li><strong>USB Control:</strong> Connect directly to your computer via USB for stable, wired control.</li>
                  <li><strong>On-board Automation:</strong> Program the ESP32 for autonomous behavior, allowing your robot to operate independently.</li>
                </ul>
              </p>
            </div>
            {/* Desktop: Carousel on the right */}
            <div className="hidden md:block w-80 flex-shrink-0 ml-8">
              <div className="relative overflow-hidden rounded-lg" style={{ aspectRatio: '1/1' }}>
                <img
                  src={images[currentImageIndex]}
                  alt={`Smart Servo Add-On Board ${currentImageIndex + 1}`}
                  className="w-full h-full object-cover object-center transition-opacity duration-500"
                  style={{
                    objectPosition: currentImageIndex === 1 ? '35% center' : 'center center'
                  }}
                />
                <button
                  onClick={() => setCurrentImageIndex((prevIndex) => (prevIndex - 1 + images.length) % images.length)}
                  className="absolute left-2 top-1/2 transform -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white p-2 rounded-full transition-colors"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                </button>
                <button
                  onClick={() => setCurrentImageIndex((prevIndex) => (prevIndex + 1) % images.length)}
                  className="absolute right-2 top-1/2 transform -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white p-2 rounded-full transition-colors"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="flex flex-col md:flex-row gap-16 mt-12 ml-6 md:ml-12">
          <div>
            <iframe
              width="315"
              height="560"
              src="https://www.youtube.com/embed/vOy0YbSvCjA"
              title="Smart Servo Add-On Board Demo"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            ></iframe>
          </div>

          <div className="mr-8 mt-12 w-96">
            <ul className="list-disc list-outside space-y-2">
              <li>Buy on <a href="https://www.tindie.com/products/robotstack/smart-servo-add-on-board/" className="whitespace-nowrap">Tindie</a></li>
              <li>Documentation on <a href="https://github.com/robotstack-dev/smart-servo-addon-board" className="whitespace-nowrap">GitHub</a></li>
              <li><a href="https://github.com/robotstack-dev/smart-servo-bridge" className="whitespace-nowrap">Smart Servo Bridge</a> Arduino library with<br /> examples for <span className="whitespace-nowrap">WebSocket server</span> and more</li>
              <li><a href="https://github.com/robotstack-dev/ftservo-python-websockets" className="whitespace-nowrap">Feetech python library</a> with option for <span className="whitespace-nowrap">WebSocket client</span></li>
              <li><a href="https://github.com/robotstack-dev/dynamixel-python-sdk-websockets" className="whitespace-nowrap">Dynamixel python library</a> with option for <span className="whitespace-nowrap">WebSocket client</span></li>
            </ul>
          </div>
        </div>
      </div>
    </section>
  )
}

export default MainContent 